export type SlotMediaType = 'image' | 'video' | 'audio';

const EXTENSION_TO_TYPE: Record<string, SlotMediaType> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  webp: 'image',
  gif: 'image',
  bmp: 'image',
  svg: 'image',
  mp4: 'video',
  mov: 'video',
  mpeg: 'video',
  mpg: 'video',
  webm: 'video',
  avi: 'video',
  mkv: 'video',
  mp3: 'audio',
  wav: 'audio',
  m4a: 'audio',
  aac: 'audio',
  ogg: 'audio',
  flac: 'audio',
};

function getTypeFromMime(mimeType?: string | null): SlotMediaType | null {
  if (!mimeType) return null;
  const lowered = mimeType.toLowerCase();
  if (lowered.startsWith('image/')) return 'image';
  if (lowered.startsWith('video/')) return 'video';
  if (lowered.startsWith('audio/')) return 'audio';
  return null;
}

function getTypeFromFilename(filename?: string | null): SlotMediaType | null {
  if (!filename) return null;
  const parts = filename.toLowerCase().split('.');
  const ext = parts.length > 1 ? parts[parts.length - 1] : '';
  return EXTENSION_TO_TYPE[ext] ?? null;
}

function mediaLabel(type: SlotMediaType): string {
  if (type === 'image') return 'image';
  if (type === 'video') return 'video';
  return 'audio';
}

function withArticle(type: SlotMediaType): string {
  return type === 'image' ? 'an image' : `a ${mediaLabel(type)}`;
}

export function getSlotTypeMismatchMessage(requiredType: SlotMediaType): string {
  return `This slot requires ${withArticle(requiredType)}. Please choose ${withArticle(requiredType)} file.`;
}

export function getUnknownSlotTypeMessage(requiredType: SlotMediaType): string {
  return `Could not determine file type. This slot requires ${withArticle(requiredType)} file.`;
}

export function validateAssetTypeForSlot(
  requiredType: SlotMediaType,
  input: {
    mimeType?: string | null;
    filename?: string | null;
    declaredType?: SlotMediaType | null;
  },
): { valid: boolean; detectedType: SlotMediaType | null; message?: string } {
  const detectedType =
    getTypeFromMime(input.mimeType) ??
    getTypeFromFilename(input.filename) ??
    input.declaredType ??
    null;

  if (!detectedType) {
    return {
      valid: false,
      detectedType: null,
      message: getUnknownSlotTypeMessage(requiredType),
    };
  }

  if (detectedType !== requiredType) {
    return {
      valid: false,
      detectedType,
      message: getSlotTypeMismatchMessage(requiredType),
    };
  }

  return { valid: true, detectedType };
}
